import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { PERFORMANCE } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine, ReferenceArea, Tooltip } from "recharts";
import { Plus, TrendingUp, TrendingDown, CheckCircle2, AlertTriangle, Wrench } from "lucide-react";
import { toast } from "sonner";

const WEATHER_BANDS = [
  { kind: "haze", from: "2023-09", to: "2023-10" },
  { kind: "monsoon", from: "2023-12", to: "2024-01" },
  { kind: "haze", from: "2025-08", to: "2025-09" },
  { kind: "monsoon", from: "2025-12", to: "2026-01" },
];
const BAND_FILL = { haze: "#F59E0B", monsoon: "#3B82F6" };

function weatherAreas(periods) {
  if (!periods || periods.length === 0) return [];
  const areas = [];
  WEATHER_BANDS.forEach((b, i) => {
    const within = periods.filter((p) => p >= b.from && p <= b.to);
    if (within.length === 0) return;
    areas.push(
      <ReferenceArea key={`wb-${i}`} x1={within[0]} x2={within[within.length - 1]}
        fill={BAND_FILL[b.kind]} fillOpacity={0.14} strokeOpacity={0} ifOverflow="extendDomain" />
    );
  });
  return areas;
}

export default function PerformanceWorkspace() {
  const { hasRole } = useAuth();
  const ws = useWorkspace();
  const contractId = ws.contractId;
  const canEdit = hasRole("tenant_admin", "contract_manager", "supervisor");
  const [inds, setInds] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [reading, setReading] = useState({ open: false, id: null, period: "", value: "" });
  const [corrective, setCorrective] = useState({ open: false, id: null, description: "", owner: "" });

  const load = useCallback(async () => {
    if (!contractId) return;
    const r = await api.get(`/indicators?contract_id=${contractId}`);
    setInds(r.data);
  }, [contractId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.post("/indicators", { contract_id: contractId, name: form.name, category: form.category || "nature", unit: form.unit || "", target: form.target ? Number(form.target) : null, direction: form.direction || "higher_better" });
      toast.success("Indicator added"); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const addReading = async () => {
    try {
      await api.post(`/indicators/${reading.id}/readings`, { period: reading.period, value: Number(reading.value) });
      toast.success("Reading logged"); setReading({ open: false, id: null, period: "", value: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const addCorrective = async () => {
    try {
      await api.post(`/indicators/${corrective.id}/corrective-action`, { description: corrective.description, owner: corrective.owner });
      toast.success("Corrective action assigned"); setCorrective({ open: false, id: null, description: "", owner: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!contractId) return <p className="text-sm text-muted-foreground">Select a contract to monitor its indicators.</p>;

  return (
    <div className="space-y-4" data-testid={PERFORMANCE.root}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Nature · People · Business indicators for this contract</p>
        {canEdit && <Button size="sm" onClick={() => { setForm({}); setOpen(true); }} data-testid={PERFORMANCE.addBtn} className="bg-forest-mid hover:bg-forest-dark"><Plus className="mr-1 h-4 w-4" /> Add indicator</Button>}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground" data-testid={PERFORMANCE.weatherLegend}>
        <span className="font-medium text-forest-dark">Weather overlay:</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-4 rounded-sm" style={{ backgroundColor: "#F59E0B", opacity: 0.4 }} /> Haze (Aug–Oct)</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-4 rounded-sm" style={{ backgroundColor: "#3B82F6", opacity: 0.4 }} /> Monsoon (Dec–Jan)</span>
        <span>— shaded periods line up with the dips they caused</span>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {inds.length === 0 && <p className="text-sm text-muted-foreground">No indicators yet.</p>}
        {inds.map((ind) => {
          const data = (ind.readings || []).map((r) => ({ period: r.period, value: r.value }));
          const onTrack = ind.on_track;
          return (
            <Card key={ind.id} className="border-sage-light p-5" data-testid={PERFORMANCE.card(ind.id)}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={ind.category} kind="category" />
                    {onTrack === true && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> On track</span>}
                    {onTrack === false && <span className="flex items-center gap-1 text-xs font-semibold text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> Off target</span>}
                  </div>
                  <h3 className="mt-1.5 font-heading text-base font-semibold text-forest-dark">{ind.name}</h3>
                </div>
                <div className="text-right">
                  <p className="font-heading text-2xl font-bold text-forest-dark">{ind.latest_value ?? "—"}<span className="text-sm font-normal text-muted-foreground"> {ind.unit}</span></p>
                  <p className="text-xs text-muted-foreground">target {ind.target ?? "—"}{ind.direction === "lower_better" ? " ↓" : " ↑"}</p>
                </div>
              </div>

              {data.length > 0 && (
                <ResponsiveContainer width="100%" height={120} className="mt-3">
                  <LineChart data={data} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
                    <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    {weatherAreas(data.map((d) => d.period))}
                    <Tooltip />
                    {ind.target != null && <ReferenceLine y={ind.target} stroke="#C86D51" strokeDasharray="4 3" />}
                    <Line type="monotone" dataKey="value" stroke="#27523C" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}

              {(ind.corrective_actions || []).length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {ind.corrective_actions.map((c) => (
                    <div key={c.id} className="flex items-start gap-2 rounded-lg bg-terracotta-light p-2 text-xs">
                      <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-terracotta" />
                      <span className="min-w-0 flex-1 text-forest-dark"><b>{c.description}</b>{c.owner ? ` — ${c.owner}` : ""}</span>
                      {c.status && (
                        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${c.status === "resolved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                          {c.status === "resolved" ? "Resolved" : "Open"}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {canEdit && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" className="h-8 flex-1 text-xs" onClick={() => setReading({ open: true, id: ind.id, period: "", value: "" })} data-testid={PERFORMANCE.addReadingBtn(ind.id)}>Log reading</Button>
                  <Button size="sm" variant="outline" className="h-8 flex-1 text-xs" onClick={() => setCorrective({ open: true, id: ind.id, description: "", owner: "" })} data-testid={PERFORMANCE.correctiveBtn(ind.id)}>Corrective action</Button>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* add indicator */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Add indicator</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={PERFORMANCE.field("name")} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={form.category || "nature"} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid={PERFORMANCE.field("category")}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="nature">Nature</SelectItem><SelectItem value="people">People</SelectItem><SelectItem value="business">Business</SelectItem></SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Unit</Label><Input value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="%, m³, days" data-testid={PERFORMANCE.field("unit")} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Target</Label><Input type="number" value={form.target ?? ""} onChange={(e) => setForm({ ...form, target: e.target.value })} data-testid={PERFORMANCE.field("target")} /></div>
              <div className="space-y-1.5"><Label>Direction</Label>
                <Select value={form.direction || "higher_better"} onValueChange={(v) => setForm({ ...form, direction: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="higher_better">Higher is better</SelectItem><SelectItem value="lower_better">Lower is better</SelectItem></SelectContent></Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-forest-mid hover:bg-forest-dark" data-testid={PERFORMANCE.saveBtn}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* reading */}
      <Dialog open={reading.open} onOpenChange={(v) => setReading({ ...reading, open: v })}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Log reading</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Period (YYYY-MM)</Label><Input value={reading.period} onChange={(e) => setReading({ ...reading, period: e.target.value })} placeholder="2026-06" /></div>
            <div className="space-y-1.5"><Label>Value</Label><Input type="number" value={reading.value} onChange={(e) => setReading({ ...reading, value: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={addReading} className="bg-forest-mid hover:bg-forest-dark">Save reading</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* corrective */}
      <Dialog open={corrective.open} onOpenChange={(v) => setCorrective({ ...corrective, open: v })}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Assign corrective action</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Action</Label><Input value={corrective.description} onChange={(e) => setCorrective({ ...corrective, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Owner</Label><Input value={corrective.owner} onChange={(e) => setCorrective({ ...corrective, owner: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={addCorrective} className="bg-forest-mid hover:bg-forest-dark">Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
